/**
 * "Plan and usage" gadget on the project Home: the person's plan and how much of their AI credits is used this month
 * (`GET /api/v1/me/credits`, utils/creditsModel.js). Credits are the person's, not the project's, so the gadget says
 * "your". A figure the gateway does not send is not shown.
 */
import React, { useEffect, useState } from 'react';
import usePlanFeatures from '../../hooks/usePlanFeatures';
import { getMyCredits } from '../../services/CreditsService';
import { creditsAmount, hasCredits, spendLine } from '../../utils/creditsModel';
import { PlanChangeButton } from '../sandbox/PlanRequest';

/** Whole percent of the allowance used (0-100), or null without an allowance. */
export function usedPercent(me) {
  const left = me?.balance?.left;
  const of = me?.balance?.of;
  if (!hasCredits(me) || typeof of !== 'number' || of <= 0) { return null; }
  return Math.max(0, Math.min(100, Math.round(((of - left) / of) * 100)));
}

export default function PlanUsage() {
  const { planName } = usePlanFeatures();
  const [credits, setCredits] = useState({ me: null, loading: true, failed: false });
  useEffect(() => {
    let live = true;
    getMyCredits()
      .then((me) => { if (live) { setCredits({ me, loading: false, failed: false }); } })
      .catch(() => { if (live) { setCredits({ me: null, loading: false, failed: true }); } });
    return () => { live = false; };
  }, []);

  const { me, loading, failed } = credits;
  const percent = usedPercent(me);
  const spend = spendLine(me, 'en');
  return (
    <div className="ph-card" data-testid="plan-usage">
      <div className="ph-card-head">
        <h2 className="ph-card-title">Plan and usage</h2>
        <span className="ph-card-links"><PlanChangeButton /></span>
      </div>
      <p className="pu-line">{planName ? <><strong>{planName}</strong> plan</> : 'Your plan'}</p>
      {!hasCredits(me) ? (
        <p className="ph-quiet">{loading ? 'Reading your credits…' : failed ? 'Your credits could not be read just now.' : 'No credit balance to show for this plan.'}</p>
      ) : (
        <>
          <p className="ph-small" data-testid="plan-usage-credits">
            AI credits this month: {percent === null ? `${creditsAmount(me.balance.left)} left` : `${percent}% used`}
          </p>
          {percent !== null && <div aria-hidden="true" className="rp-bar"><div className="rp-fill" style={{ width: `${percent}%` }} /></div>}
          {spend && <p className="ph-muted ph-small rp-more">{spend}</p>}
        </>
      )}
    </div>
  );
}

